import { useQuery, type QueryClient } from "@tanstack/react-query";
import { listAirlines, getAirline } from "./api";
import type { Airline, AirlineListQuery, PaginatedAirlineItems } from "./types";

export const AirlinesKeys = {
  all: ["airlines"] as const,
  list: (query: AirlineListQuery = {}) => ["airlines", "list", query] as const,
  detail: (id: number | string) => ["airlines", "detail", String(id)] as const,
};

export function useAirlinesList(query: AirlineListQuery = {}) {
  return useQuery<PaginatedAirlineItems, Error>({
    queryKey: AirlinesKeys.list(query),
    queryFn: () => listAirlines(query),
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function useAirlineDetail(id: number | null) {
  const enabled = id != null;
  return useQuery<Airline, Error>({
    queryKey: enabled ? AirlinesKeys.detail(id as number) : (["airlines", "detail", "disabled"] as const),
    queryFn: () => {
      if (id == null) return Promise.reject(new Error("Airline id is required"));
      return getAirline(id);
    },
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

export function prefetchAirlines(qc: QueryClient, query: AirlineListQuery = {}) {
  return qc.prefetchQuery({
    queryKey: AirlinesKeys.list(query),
    queryFn: () => listAirlines(query),
    staleTime: 30_000,
  });
}
